import React, { useEffect, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Video } from '@/lib/api/types';
import { VideoApi } from '@/lib/api/videoApi';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatFileSize } from '@/lib/utils';
import VideoProcessingStatus from '@/components/VideoProcessingStatus';
import { useVideo, useVideoSignedUrls } from '@/hooks/useVideoCache';

const VideoDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { session } = useAuth();
  const [currentTime, setCurrentTime] = useState(0);

  // Use React Query for video data
  const { data: video, isLoading, error } = useVideo(id || '');
  
  // Use signed URLs hook for playback URLs
  const { data: videoWithUrls } = useVideoSignedUrls(id || '');

  // Parse timestamp from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const timestamp = urlParams.get('t');
    if (timestamp) {
      const time = parseFloat(timestamp);
      setCurrentTime(time);
    }
  }, [location.search]);

  // Combine basic video data with signed URLs
  const finalVideo = videoWithUrls || video;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="h-96 rounded-lg bg-muted animate-pulse mb-6"></div>
        <div className="h-12 w-1/3 bg-muted animate-pulse mb-4"></div>
        <div className="h-6 w-1/2 bg-muted animate-pulse mb-8"></div>
        <div className="h-64 bg-muted animate-pulse rounded"></div>
      </div>
    );
  }

  if (!finalVideo) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <div className="py-12 flex-1 text-center">
          <h1 className="text-2xl font-bold mb-4">Video not found</h1>
          <p className="text-muted-foreground mb-6">The video you're looking for doesn't exist or has been removed.</p>
          <Button asChild>
            <Link to="/videos">Return to Your Videos</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Button variant="ghost" asChild className="pl-0 mb-4">
        <Link to="/videos" className="flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" />
          Back to Your Videos
        </Link>
      </Button>
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-3xl font-bold">{finalVideo.title}</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {finalVideo.playback_url ? (
            <video
              controls
              src={finalVideo.playback_url}
              className="w-full rounded mb-4"
              poster={finalVideo.thumbnail_url ? finalVideo.thumbnail_url : undefined}
              autoPlay={false}
              onError={(e) => {
                console.error('Video playback error:', e);
              }}
            />
          ) : (
            <div className="mb-4 p-8 text-center border-2 border-dashed border-gray-300 rounded-lg">
              <div className="text-gray-500 mb-2">No playback URL available</div>
              <div className="text-sm text-gray-400">
                {finalVideo.status === 'COMPLETED' 
                  ? 'Video processing completed but playback URL is not available. Please try refreshing the page.'
                  : 'Video is still being processed. Playback will be available once processing is complete.'
                }
              </div>
            </div>
          )}
          <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-muted-foreground">
                Uploaded on {new Date(finalVideo.created_at).toLocaleDateString()}
              </p>
              {currentTime > 0 && (
                <p className="text-sm text-video-blue mt-1">
                  Starting from {Math.floor(currentTime / 60)}:{(currentTime % 60).toFixed(0).padStart(2, '0')}
                </p>
              )}
              {finalVideo.playback_url && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Streaming from your backend
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="lg:col-span-1">
          <div className="space-y-6">
            {/* Video Processing Status */}
            <VideoProcessingStatus 
              videoId={finalVideo?.id || ''}
              onStatusChange={(status) => {
                // Status updates are handled by the cache system
                console.log('Processing status updated:', status);
              }}
            />
            
            {/* Video Information */}
            <div className="border rounded-lg overflow-hidden p-6">
              <div className="mb-2">
                <strong>Description:</strong> {finalVideo.description || "No description provided."}
              </div>
              <div className="mb-2">
                <strong>File Name:</strong> {finalVideo.file_name}
              </div>
              <div className="mb-2">
                <strong>File Size:</strong> {formatFileSize(finalVideo.file_size)}
              </div>
              <div className="mb-2">
                <strong>Status:</strong> {finalVideo.status}
              </div>
              <div className="mb-2">
                <strong>Uploaded:</strong> {new Date(finalVideo.created_at).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoDetail;
